import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Checkbox, Tag, Button, Input, Space, TableColumnType, Tooltip, notification, Modal} from 'antd';
import CustomTable from '../components/CustomTable';
import {IngredientDataType} from '../models/column';
import {ColumnsType} from 'antd/es/table';
import {useNavigate} from 'react-router-dom';
import {productsApi} from '../apiCalls/productsApi';
import {DeleteOutlined, EditOutlined, FilePdfOutlined, SearchOutlined} from '@ant-design/icons';
import {colors} from '../theme';
import PageTitle from '../components/PageTitle';
import PageLoading from '../components/PageLoading';
import type {FilterValue, SorterResult} from 'antd/es/table/interface';
import TableFiltersBar, {
  applyFiltersToColumns,
  TableFiltersState,
  useTableFilters,
} from '../components/TableFilters';

const DEFAULT_PAGE_SIZE = 50;

const SEARCHABLE_FIELDS = ['name', 'umbrellaId', 'locked', 'claim', 'functionality'] as const;
const SORTABLE_FIELDS = new Set(['name', 'umbrellaId', 'locked']);

const parseBooleanQuery = (rawQuery: string): boolean | null => {
  const query = String(rawQuery ?? '').trim().toLowerCase();
  if (!query) return null;

  const trueValues = new Set(['1', 'true', 'yes', 'si', 'sí', 'locked', 'on']);
  const falseValues = new Set(['0', 'false', 'no', 'unlock', 'unlocked', 'off']);

  if (trueValues.has(query)) return true;
  if (falseValues.has(query)) return false;
  return null;
};

const firstFilterValue = (value: FilterValue | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  return s === '' ? null : s;
};

const buildBackendFilters = (state: TableFiltersState): Record<string, string> => {
  const out: Record<string, string> = {};
  SEARCHABLE_FIELDS.forEach((field) => {
    const raw = firstFilterValue(state[field]);
    if (raw === null) return;

    if (field === 'locked') {
      const bool = parseBooleanQuery(raw);
      if (bool === null) return;
      out.locked = bool ? '1' : '0';
      return;
    }

    out[field] = raw;
  });
  return out;
};

const CustomFormTable: React.FC = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState<ColumnsType<IngredientDataType>>([]);
  const [dataSource, setDataSource] = useState<IngredientDataType[]>([]);
  const [deleting, setDeleting] = useState<number | null>(null);

  const [pagination, setPagination] = useState({current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0});
  const [sorter, setSorter] = useState<{field?: string; order?: 'asc' | 'desc'}>({});

  const {filters, setFilters, clearAllFilters, removeFilterValue} = useTableFilters<IngredientDataType>();

  const navigate = useNavigate();
  const searchInput = useRef<any>(null);
  const columnsBuilt = useRef(false);

  const getColumnSearchProps = useCallback(
    (dataIndex: string): TableColumnType<any> => ({
      filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
        <div style={{padding: 8}} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={dataIndex === 'locked' ? 'Search locked (si/no)' : `Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{marginBottom: 8, display: 'block'}}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{width: 90}}
            >
              Buscar
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                clearFilters && clearFilters();
                confirm({closeDropdown: false});
                close();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}} />
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    }),
    [],
  );

  const buildColumns = useCallback(
    (sample: Record<string, unknown>): ColumnsType<IngredientDataType> => {
      const allKeys = Object.keys(sample).filter((key) => key !== 'id');

      const mediumColumns = ['quantity', 'price'];
      const autoWidthColumns2 = ['name', 'listOfIngredients', 'latinName'];
      const wideColumns = ['internalName'];
      const autoWidthColumns = ['productAccordingTo', 'productAllergens'];

      const capitalizeFirstLetter = (s: string) =>
        s === 'umbrellaId' ? 'ID' : s.charAt(0).toUpperCase() + s.slice(1);

      return [
        {
          title: 'Actions',
          key: 'actions',
          fixed: 'left',
          width: 100,
          align: 'center',
          render: (_: any, record: IngredientDataType) => (
            <Space size={0}>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  size="small"
                  style={{color: colors.primary}}
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/ingredients/${record.id}/edit`)}
                />
              </Tooltip>
              <Tooltip title="Download PDF">
                <Button
                  type="text"
                  size="small"
                  style={{color: colors.primary}}
                  icon={<FilePdfOutlined />}
                  href={`https://api-dev.formix.cloud/pdf/product/${record.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  danger
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(record)}
                  loading={deleting === record.id}
                />
              </Tooltip>
            </Space>
          ),
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ...allKeys.map((item) => ({
          title: capitalizeFirstLetter(item),
          key: item,
          dataIndex: item,
          width: autoWidthColumns.includes(item)
            ? 800
            : wideColumns.includes(item)
              ? 500
              : autoWidthColumns2.includes(item)
                ? 400
                : mediumColumns.includes(item)
                  ? 150
                  : 100,
          fixed: item === 'name' || item === 'umbrellaId' ? 'left' : undefined,
          ...(SORTABLE_FIELDS.has(item) ? {sorter: true} : {}),
          ...((SEARCHABLE_FIELDS as readonly string[]).includes(item) ? getColumnSearchProps(item) : {}),
          align: 'center',
          render: (value: any) => {
            if (typeof value === 'boolean') {
              return <Checkbox checked={value} />;
            }

            if (value?.name) {
              return value.name;
            }

            if (Array.isArray(value) && value.length > 0) {
              if (value[0]?.name) {
                return (
                  <span style={{whiteSpace: 'nowrap'}}>
                    {value.map((v: any, idx: number) => (
                      <Tag color="blue" key={v.id ?? idx}>
                        {v?.name?.length > 20 ? v?.name?.substring(0, 18) + '...' : v?.name}
                      </Tag>
                    ))}
                  </span>
                );
              }

              if (item === 'actives') {
                const toolTipContent = value.map((entry: any, index: number) => {
                  const text = `•${entry?.activeName?.name} ${entry.activeValue}%`;
                  return (
                    <div
                      key={index}
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '1000px',
                      }}
                    >
                      {text}
                    </div>
                  );
                });

                return (
                  <Tooltip title={<span>{toolTipContent}</span>} overlayStyle={{maxWidth: 1000}}>
                    <Tag color="blue" key={item}>
                      {value.length === 1 ? 'Hay 1 valor' : `Hay ${value.length} valores`}
                    </Tag>
                  </Tooltip>
                );
              }

              return (
                <Tag color="blue" key={item}>
                  {value.length === 1 ? 'Hay 1 valor' : `Hay ${value.length} valores`}
                </Tag>
              );
            }

            if (value != null && typeof value === 'object') {
              return String(value);
            }
            return value;
          },
        })),
      ];
    },
    [deleting, getColumnSearchProps, navigate],
  );

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productsApi.getProducts({
        page: pagination.current,
        perPage: pagination.pageSize,
        filters: buildBackendFilters(filters),
        sortField: sorter.field,
        sortOrder: sorter.order,
      });

      if (!columnsBuilt.current && res.data.length > 0) {
        setColumns(buildColumns(res.data[0] as unknown as Record<string, unknown>));
        columnsBuilt.current = true;
      }

      setDataSource(res.data.map((item: any) => ({...item, id: item.id})));
      setPagination((p) => ({...p, total: res.total}));
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [buildColumns, filters, pagination.current, pagination.pageSize, sorter.field, sorter.order]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleTableChange = useCallback(
    (
      pag: {current?: number; pageSize?: number},
      nextFilters: Record<string, FilterValue | null>,
      nextSorter: SorterResult<IngredientDataType> | SorterResult<IngredientDataType>[],
    ) => {
      const filtersChanged = JSON.stringify(nextFilters) !== JSON.stringify(filters);

      const single = Array.isArray(nextSorter) ? nextSorter[0] : nextSorter;
      const nextSortField = single?.field ? String(single.field) : undefined;
      const nextSortOrder =
        single?.order === 'ascend' ? 'asc' : single?.order === 'descend' ? 'desc' : undefined;

      const sortChanged = nextSortField !== sorter.field || nextSortOrder !== sorter.order;

      setFilters(nextFilters as TableFiltersState);
      setSorter({field: nextSortField, order: nextSortOrder});

      setPagination((prev) => ({
        ...prev,
        current: filtersChanged || sortChanged ? 1 : pag.current ?? prev.current,
        pageSize: pag.pageSize ?? prev.pageSize,
      }));
    },
    [filters, setFilters, sorter.field, sorter.order],
  );

  const handleDelete = async (id: number) => {
    try {
      setDeleting(id);
      const ok = await productsApi.deleteProduct(String(id));
      if (ok) {
        notification.success({
          message: 'Product deleted',
          description: 'The product has been removed successfully.',
          duration: 3,
        });
        fetchProducts();
      }
    } catch (error: any) {
      notification.error({
        message: 'Error while deleting',
        description: error.message ?? 'Unknown error',
        duration: 5,
      });
    } finally {
      setDeleting(null);
    }
  };

  const showDeleteConfirm = (record: IngredientDataType) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this product?',
      content: 'If this product is referenced in a formulation or ingredient it cannot be removed.',
      okText: 'Yes, delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(record.id);
      },
    });
  };

  const tableColumns = useMemo(() => applyFiltersToColumns(columns, filters), [columns, filters]);

  if (initialLoading) {
    return <PageLoading />;
  }

  return (
    <div style={{padding: '24px 32px'}}>
      <PageTitle title="Ingredients" />
      <Button
        type="primary"
        onClick={() => navigate('/ingredients/new')}
        style={{marginBottom: 16}}
      >
        Add New Ingredient
      </Button>
      <TableFiltersBar
        columns={tableColumns}
        filters={filters}
        onClearAll={clearAllFilters}
        onRemove={removeFilterValue}
      />
      <CustomTable
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          pageSizeOptions: [10, 25, 50, 100],
          showSizeChanger: true,
          showTotal: (total) => `Total: ${total}`,
        }}
        columns={tableColumns}
        dataSource={dataSource}
        loading={loading}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default CustomFormTable;
